<template>
  <div
    class="buttons-container d-flex align-center fill-width background-top-bar-background"
    :class="{
      'justify-end': !$vuetify.display.mobile,
      'justify-space-around': $vuetify.display.mobile,
      'flex-shrink-1': !$vuetify.display.mobile
    }"
  >
    <nav-square-button
      v-if="$vuetify.display.mobile"
      :aria-label="$translate('NavButtons.ariaHome')"
      :to="{ name: $routes.ROOT }"
      :active="activeButtons.includes(navButtonTypes.HOME)"
      @click.native="onButtonClick(navButtonTypes.HOME)"
    >
      <template #active>
        <i-custom-home-filled />
      </template>
      <template #inactive>
        <i-custom-home />
      </template>
    </nav-square-button>
    <nav-square-button v-else-if="manageUiDisplayed" href="/manage" :aria-label="$translate('NavButtons.ariaManage')">
      <template #inactive>
        <i-custom-disciple />
      </template>
    </nav-square-button>

    <template v-if="$vuetify.display.mobile || showDesktopNavButtons">
      <nav-messages-button
        v-if="configuration.friendsMessagingEnabled"
        :aria-label="$translate('NavButtons.ariaMessages')"
        :active="activeButtons.includes(navButtonTypes.MESSAGES)"
        @click.native="onButtonClick(navButtonTypes.MESSAGES)"
      />

      <nav-inbox-button
        v-if="!$vuetify.display.mobile"
        :active="activeButtons.includes(navButtonTypes.INBOX)"
        :aria-label="$translate('NavButtons.ariaNotifications')"
        @click.native="onButtonClick(navButtonTypes.INBOX)"
        @keydown.native.exact.enter="setInboxDisplayed(!inboxDisplayed)"
      />

      <nav-square-button
        :to="{ name: $routes.PROFILE }"
        :active="activeButtons.includes(navButtonTypes.PROFILE)"
        :aria-label="$translate('NavButtons.ariaProfile')"
        @click.native="onButtonClick(navButtonTypes.PROFILE)"
      >
        <template #active>
          <user-avatar v-if="user && user.avatar" :user="user" :size="24" />
          <i-custom-profile-filled v-else />
        </template>
        <template #inactive>
          <user-avatar v-if="user && user.avatar" :user="user" :size="24" />
          <i-custom-profile v-else />
        </template>
      </nav-square-button>
    </template>

    <nav-inbox-button
      v-if="$vuetify.display.mobile"
      :active="activeButtons.includes(navButtonTypes.INBOX)"
      :aria-label="$translate('NavButtons.ariaNotifications')"
      @click.native="onButtonClick(navButtonTypes.INBOX)"
      @keydown.native.exact.enter="setInboxDisplayed(!inboxDisplayed)"
    />

    <nav-square-button
      v-if="$vuetify.display.mobile"
      :active="activeButtons.includes(navButtonTypes.MORE_MENU)"
      :aria-label="$translate('NavButtons.ariaMore')"
      @click.native="onButtonClick(navButtonTypes.MORE_MENU)"
    >
      <template #active>
        <i-custom-three-lines-filled />
      </template>
      <template #inactive>
        <i-custom-three-lines />
      </template>
    </nav-square-button>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "pinia"

import { useAppStateStore } from "@/store/appState.js"
import { useUserSessionStore } from "@/store/userSession.js"
import { useConfigurationStore } from "@/store/configuration.js"

import NavSquareButton from "@/components/navs/NavSquareButton.vue"
import NavInboxButton from "@/components/navs/NavInboxButton.vue"
import NavMessagesButton from "@/components/navs/NavMessagesButton.vue"

const navButtonTypes = {
  HOME: "home",
  MESSAGES: "messages",
  INBOX: "inbox",
  PROFILE: "profile",
  MORE_MENU: "moreMenu"
}

export default {
  components: {
    NavSquareButton,
    NavInboxButton,
    NavMessagesButton
  },
  props: {
    showDesktopNavButtons: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      navButtonTypes
    }
  },
  computed: {
    ...mapGetters(useUserSessionStore, ["user", "isLoggedIn", "manageUiDisplayed"]),
    ...mapGetters(useAppStateStore, ["moreMenuDisplayed", "inboxDisplayed"]),
    ...mapGetters(useConfigurationStore, ["configuration"]),
    moreMenuDisplayedOnMobile() {
      return this.$vuetify.display.mobile && this.moreMenuDisplayed
    },
    moreMenuOrInboxDialogDisplayedOnMobile() {
      return this.$vuetify.display.mobile && (this.moreMenuDisplayed || this.inboxDisplayed)
    },
    isOnCurrentUserProfilePage() {
      return this.$route.name === this.$routes.PROFILE && (!this.$route.params.id || this.$route.params.id === this.user?.id)
    },
    activeButtons() {
      const ret = []
      if (this.moreMenuOrInboxDialogDisplayedOnMobile) {
        if (!this.inboxDisplayed && this.moreMenuDisplayedOnMobile) {
          ret.push(navButtonTypes.MORE_MENU)
        }
      } else if (this.$routerState.isRoot) {
        ret.push(navButtonTypes.HOME)
      } else if (this.isOnCurrentUserProfilePage || [this.$routes.PROFILE_EDIT, this.$routes.PROFILE_SETTINGS].includes(this.$route.name)) {
        ret.push(navButtonTypes.PROFILE)
      } else if (this.$route.name === this.$routes.MESSAGES) {
        ret.push(navButtonTypes.MESSAGES)
      }

      if (this.inboxDisplayed) {
        ret.push(navButtonTypes.INBOX)
      }

      return ret
    }
  },
  watch: {
    moreMenuDisplayedOnMobile(newVal) {
      if (newVal) {
        this.setInboxDisplayed(false)
      }
    }
  },
  methods: {
    ...mapActions(useAppStateStore, ["setMoreMenuDisplayed", "setInboxDisplayed"]),
    onButtonClick(name) {
      if (name !== this.navButtonTypes.INBOX) {
        this.setInboxDisplayed(false)
      }

      if (name !== this.navButtonTypes.MORE_MENU && this.$vuetify.display.mobile) {
        this.setMoreMenuDisplayed(false)
      }

      if (name === this.navButtonTypes.MORE_MENU) {
        if (this.isLoggedIn) {
          this.setMoreMenuDisplayed(!this.moreMenuDisplayed)
        } else {
          this.$router.push({ name: this.$routes.LOGIN })
        }
      }

      if (name === this.navButtonTypes.INBOX) {
        const inboxAlreadyShown = this.inboxDisplayed
        this.setInboxDisplayed(true)

        if (this.$vuetify.display.mobile) {
          this.$eventBus.$emit("inbox-button-mobile-click", inboxAlreadyShown)
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.app-icon {
  height: 24px;
  width: 24px;
}

.buttons-container {
  :deep(.v-btn.v-btn--icon) {
    width: 64px;
    height: 64px !important;
  }
}
</style>
